/**
 * Zukan Data Loader
 * Fetches fish data from Shopify Metaobjects using Storefront API
 */

class ZukanDataLoader {
  constructor(options = {}) {
    this.shopDomain = options.shopDomain || window.Shopify?.shop?.split('.')[0] || '';
    this.storefrontAccessToken = options.storefrontAccessToken || '';
    this.apiVersion = options.apiVersion || '2024-01';
    this.metaobjectType = options.metaobjectType || 'fish';
    this.chokaType = options.chokaType || 'choka';
    // SPは6件表示。PCはCSSで6件目以降を隠し5件にする
    this.recentChokaCount = Math.max(options.recentChokaCount || 6, 6);
    this.showRecentChoka = options.showRecentChoka !== false;
    this.fishData = [];
    this.categorizedFish = {};
  }

  /**
   * Get Storefront API endpoint URL
   */
  getApiUrl() {
    if (!this.shopDomain) {
      console.error('Shop domain is required');
      return null;
    }
    return `https://${this.shopDomain}.myshopify.com/api/${this.apiVersion}/graphql.json`;
  }

  /**
   * Fetch metaobjects from Shopify Storefront API
   */
  async fetchMetaobjects() {
    const apiUrl = this.getApiUrl();
    if (!apiUrl || !this.storefrontAccessToken) {
      console.error('API URL or access token is missing');
      return [];
    }

    const query = `
      query getFishMetaobjects($type: String!) {
        metaobjects(type: $type, first: 250) {
          edges {
            node {
              id
              handle
              fields {
                key
                value
                type
                reference {
                  ... on MediaImage {
                    image {
                      url
                      altText
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
        },
        body: JSON.stringify({
          query,
          variables: {
            type: this.metaobjectType,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        return [];
      }

      return data.data?.metaobjects?.edges || [];
    } catch (error) {
      console.error('Error fetching metaobjects:', error);
      return [];
    }
  }

  /**
   * Parse metaobject fields into fish object
   */
  parseFishData(metaobjectNode) {
    const fish = {
      id: metaobjectNode.id,
      handle: metaobjectNode.handle,
      name: '',
      image: '',
      category: '',
    };

    metaobjectNode.fields.forEach((field) => {
      switch (field.key) {
        case 'name_ja':
        case 'name':
        case 'fish_name':
          fish.name = field.value || '';
          break;
        case 'image':
        case 'fish_image':
        case 'photo':
          if (field.reference?.image) {
            fish.image = field.reference.image.url;
          } else if (field.value) {
            fish.image = field.value;
          }
          break;
        case 'category':
        case 'category_kana':
        case 'kana_category':
          fish.category = field.value || '';
          break;
      }
    });

    // 画像が設定されていない場合はプレースホルダーを使用
    if (!fish.image) {
      fish.image = '/assets/fish-placeholder.jpg';
    }

    return fish;
  }

  /**
   * Get kana category from fish name (ア行、カ行など)
   */
  getKanaCategory(fishName) {
    if (!fishName) return 'other';
    
    const firstChar = fishName.charAt(0);
    const kanaMap = {
      'ア': 'a', 'イ': 'a', 'ウ': 'a', 'エ': 'a', 'オ': 'a',
      'カ': 'ka', 'キ': 'ka', 'ク': 'ka', 'ケ': 'ka', 'コ': 'ka',
      'サ': 'sa', 'シ': 'sa', 'ス': 'sa', 'セ': 'sa', 'ソ': 'sa',
      'タ': 'ta', 'チ': 'ta', 'ツ': 'ta', 'テ': 'ta', 'ト': 'ta',
      'ナ': 'na', 'ニ': 'na', 'ヌ': 'na', 'ネ': 'na', 'ノ': 'na',
      'ハ': 'ha', 'ヒ': 'ha', 'フ': 'ha', 'ヘ': 'ha', 'ホ': 'ha',
      'マ': 'ma', 'ミ': 'ma', 'ム': 'ma', 'メ': 'ma', 'モ': 'ma',
      'ヤ': 'ya', 'ユ': 'ya', 'ヨ': 'ya',
      'ラ': 'ra', 'リ': 'ra', 'ル': 'ra', 'レ': 'ra', 'ロ': 'ra',
      'ワ': 'wa', 'ヲ': 'wa', 'ン': 'wa',
    };

    return kanaMap[firstChar] || 'other';
  }

  /**
   * Categorize fish by kana
   */
  categorizeFish(fishList) {
    const categorized = {
      all: fishList,
      a: [],
      ka: [],
      sa: [],
      ta: [],
      na: [],
      ha: [],
      ma: [],
      ya: [],
      ra: [],
      wa: [],
    };

    fishList.forEach((fish) => {
      const category = fish.category || this.getKanaCategory(fish.name);
      if (categorized[category]) {
        categorized[category].push(fish);
      }
    });

    return categorized;
  }

  /**
   * Load and process fish data
   */
  async loadFishData() {
    try {
      const edges = await this.fetchMetaobjects();
      this.fishData = edges
        .map((edge) => this.parseFishData(edge.node))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'));
      this.categorizedFish = this.categorizeFish(this.fishData);
      return this.categorizedFish;
    } catch (error) {
      console.error('Error loading fish data:', error);
      return {};
    }
  }

  parseChoka(node) {
    const choka = {
      id: node.id,
      updatedAt: node.updatedAt || '',
      fishId: null,
      fishName: '',
      fishHandle: '',
      angler_name: '',
      photo: '',
      length_cm: '',
      fishing_date: '',
      fishing_location: '',
    };

    (node.fields || []).forEach((field) => {
      if (field.key === 'fish' && field.reference?.id) {
        choka.fishId = field.reference.id;
      } else if (field.key === 'fish' && field.references?.nodes?.[0]?.id) {
        choka.fishId = field.references.nodes[0].id;
      }
      if (field.key === 'angler_name') choka.angler_name = field.value || '';
      if (field.key === 'length_cm') choka.length_cm = field.value || '';
      if (field.key === 'fishing_date') choka.fishing_date = field.value || '';
      if (field.key === 'fishing_location') choka.fishing_location = field.value || '';
      if (field.key === 'photo' && field.reference?.image) {
        choka.photo = field.reference.image.url || '';
      } else if (field.key === 'photo' && field.references?.nodes?.[0]?.image) {
        choka.photo = field.references.nodes[0].image.url || '';
      }
    });

    return choka;
  }

  attachFishToChoka(choka) {
    const fish = this.fishData.find((f) => f.id === choka.fishId);
    if (fish) {
      choka.fishName = fish.name || '';
      choka.fishHandle = fish.handle || '';
    }
    return choka;
  }

  formatChokaDate(str) {
    if (!str) return '';
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  }

  async fetchRecentChoka() {
    const apiUrl = this.getApiUrl();
    if (!apiUrl || !this.storefrontAccessToken) return [];

    const query = `
      query getRecentChoka($type: String!, $first: Int!) {
        metaobjects(type: $type, first: $first, sortKey: "updated_at", reverse: false) {
          edges {
            node {
              id
              updatedAt
              fields {
                key
                value
                reference {
                  ... on Metaobject {
                    id
                  }
                  ... on MediaImage {
                    image { url altText }
                  }
                }
                references(first: 5) {
                  nodes {
                    ... on Metaobject {
                      id
                    }
                    ... on MediaImage {
                      image { url altText }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
        },
        body: JSON.stringify({
          query,
          variables: {
            type: this.chokaType,
            first: this.recentChokaCount,
          },
        }),
      });

      if (!response.ok) return [];

      const data = await response.json();
      if (data.errors) {
        console.error('GraphQL errors (recent choka):', data.errors);
        return [];
      }

      return (data.data?.metaobjects?.edges || []).map((edge) =>
        this.attachFishToChoka(this.parseChoka(edge.node))
      );
    } catch (error) {
      console.error('Error fetching recent choka:', error);
      return [];
    }
  }

  renderRecentChokaCard(choka) {
    const imgSrc = choka.photo || '';
    const anglerName = this.escapeHtml(choka.angler_name || '—');
    const fishName = this.escapeHtml(choka.fishName || '');
    const location = this.escapeHtml(choka.fishing_location || '');
    const dateStr = choka.fishing_date ? this.escapeHtml(this.formatChokaDate(choka.fishing_date)) : '';
    const lengthValue = choka.length_cm ? this.escapeHtml(choka.length_cm) : '';
    const handle = (choka.fishHandle || '').trim();
    const detailUrl = handle
      ? `${this.getDetailBasePath()}/${encodeURIComponent(handle)}?choka_id=${encodeURIComponent(choka.id)}`
      : '#';

    return `
      <a href="${detailUrl}" class="fish-card zukan-recent-choka__card" data-choka-id="${this.escapeHtml(choka.id)}">
        ${
          imgSrc
            ? `<img src="${this.escapeHtml(imgSrc)}" alt="${fishName || anglerName}" class="fish-image" width="200" height="120" loading="lazy">`
            : '<div class="fish-image fish-image--placeholder"></div>'
        }
        <div class="zukan-recent-choka__meta">
          ${fishName ? `<span class="zukan-recent-choka__fish">${fishName}</span>` : ''}
          <span class="zukan-recent-choka__angler">${anglerName}</span>
          <div class="zukan-recent-choka__info">
            <div class="zukan-recent-choka__info-main">
              ${location ? `<span class="zukan-recent-choka__location">${location}</span>` : ''}
              ${dateStr ? `<span class="zukan-recent-choka__date">${dateStr}</span>` : ''}
            </div>
            ${
              lengthValue
                ? `<span class="zukan-recent-choka__length"><span class="zukan-recent-choka__length-value">${lengthValue}</span><span class="zukan-recent-choka__length-unit">cm</span></span>`
                : ''
            }
          </div>
        </div>
      </a>
    `;
  }

  async renderRecentChoka() {
    const section = document.querySelector('.zukan-recent-choka');
    const grid = document.getElementById('zukan-recent-choka-grid');
    if (!this.showRecentChoka || !section || !grid) return;

    const list = await this.fetchRecentChoka();
    const loadingEl = grid.querySelector('.zukan-recent-choka__loading');
    if (loadingEl) loadingEl.remove();

    if (list.length === 0) {
      section.style.display = 'none';
      return;
    }

    grid.innerHTML = list.map((choka) => this.renderRecentChokaCard(choka)).join('');
    grid.classList.add('zukan-recent-choka__grid--loaded');
  }

  /**
   * Render fish card HTML
   */
  /**
   * 魚詳細ページのベースURL（メタオブジェクトの「Webページとして公開」のURLハンドルが "zukan" の場合）
   */
  getDetailBasePath() {
    return '/pages/zukan';
  }

  renderFishCard(fish) {
    const imageUrl = fish.image || '/assets/fish-placeholder.jpg';
    const escapedName = this.escapeHtml(fish.name || '');
    const handle = (fish.handle || '').trim();
    const detailUrl = handle ? `${this.getDetailBasePath()}/${encodeURIComponent(handle)}` : '#';

    return `
      <a href="${detailUrl}" class="fish-card" data-fish-id="${fish.id}" data-fish-handle="${this.escapeHtml(handle)}"${handle ? '' : ' aria-disabled="true"'}>
        <img src="${imageUrl}" alt="${escapedName}" class="fish-image" width="200" height="120" loading="lazy">
        <span class="fish-name">${escapedName}</span>
      </a>
    `;
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Render fish grid for a category
   */
  renderFishGrid(category, fishList) {
    if (!fishList || fishList.length === 0) {
      return '<div class="fish-grid-empty">このカテゴリーには魚が登録されていません</div>';
    }

    return fishList.map((fish) => this.renderFishCard(fish)).join('');
  }

  /**
   * Update all category sections with fish data
   */
  async renderAllCategories() {
    try {
      const categorized = await this.loadFishData();
      const categories = ['all', 'a', 'ka', 'sa', 'ta', 'na', 'ha', 'ma', 'ya', 'ra', 'wa'];

      categories.forEach((category) => {
        const section = document.querySelector(
          `.fish-category-section[data-category="${category}"]`
        );
        if (section) {
          const grid = section.querySelector('.fish-grid');
          if (grid) {
            // ローディング表示を削除
            const loadingElement = grid.querySelector('.fish-grid-loading');
            if (loadingElement) {
              loadingElement.remove();
            }

            const fishList = categorized[category] || [];
            if (fishList.length > 0) {
              grid.innerHTML = this.renderFishGrid(category, fishList);
            } else {
              grid.innerHTML = '<div class="fish-grid-empty">このカテゴリーには魚が登録されていません</div>';
            }
          }
        }
      });

      await this.renderRecentChoka();
    } catch (error) {
      console.error('Error rendering categories:', error);
      // エラー時はローディング表示をエラーメッセージに変更
      const grids = document.querySelectorAll('.fish-grid');
      grids.forEach((grid) => {
        const loadingElement = grid.querySelector('.fish-grid-loading');
        if (loadingElement) {
          loadingElement.textContent = 'データの読み込みに失敗しました。設定を確認してください。';
          loadingElement.classList.add('fish-grid-error');
        }
      });
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if we're on the zukan page
  const zukanContainer = document.querySelector('.zukan-container');
  if (!zukanContainer) return;

  // Get configuration from data attributes or global variables
  const config = {
    shopDomain: zukanContainer.dataset.shopDomain || window.Shopify?.shop?.split('.')[0],
    storefrontAccessToken: zukanContainer.dataset.storefrontToken || window.Shopify?.storefrontAccessToken,
    metaobjectType: zukanContainer.dataset.metaobjectType || 'fish',
    chokaType: zukanContainer.dataset.chokaType || 'choka',
    recentChokaCount: parseInt(zukanContainer.dataset.recentChokaCount, 10) || 6,
    showRecentChoka: zukanContainer.dataset.showRecentChoka !== 'false',
  };

  // Initialize loader
  const loader = new ZukanDataLoader(config);
  
  // Load and render fish data
  loader.renderAllCategories().catch((error) => {
    console.error('Failed to render fish data:', error);
  });
});

// Re-initialize on Shopify section load
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.classList.contains('zukan-container') || event.target.querySelector('.zukan-container')) {
    const zukanContainer = document.querySelector('.zukan-container');
    if (zukanContainer) {
      const config = {
        shopDomain: zukanContainer.dataset.shopDomain || window.Shopify?.shop?.split('.')[0],
        storefrontAccessToken: zukanContainer.dataset.storefrontToken || window.Shopify?.storefrontAccessToken,
        metaobjectType: zukanContainer.dataset.metaobjectType || 'fish',
        chokaType: zukanContainer.dataset.chokaType || 'choka',
        recentChokaCount: parseInt(zukanContainer.dataset.recentChokaCount, 10) || 6,
        showRecentChoka: zukanContainer.dataset.showRecentChoka !== 'false',
      };
      const loader = new ZukanDataLoader(config);
      loader.renderAllCategories().catch((error) => {
        console.error('Failed to render fish data:', error);
      });
    }
  }
});
